import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import type { BarProps } from "recharts";
import {
  CalendarDays,
  TriangleAlert,
  Sparkles,
  History,
  CloudSun,
  CloudRain,
  CloudSnow,
  Sun,
  Wind,
  CloudFog,
  CloudLightning,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useSpring, useTransform, type MotionValue } from "framer-motion";
import { aiPredictionService } from "../utils/api";
import { useLotHourlyData } from "../hooks/useLotData";
import LoadingLogo from "../components/LoadingLogo";
import Error from "../components/Error";
import Menu, { type MenuItem } from "../components/Menu";
import "./ParallelAI.css";

type PredictionSummary = {
  predictionId: string;
  lotId: string;
  targetDate: string;
  requestTs: string;
  title?: string;
};

type PredictionDetail = {
  predictionId: string;
  lotId: string;
  targetDate: string;
  title?: string;
  predictedVehicles: number;
  peakHour?: number;
  recommendedPatrolWindow?: string;
  factorsWeatherDeltaPct?: number;
  factorsAlertsDeltaPct?: number;
  factorsEventsDeltaPct?: number;
  factorsOtherDeltaPct?: number;
  weatherDesc?: string;
  alertsDesc?: string;
  eventsDesc?: string;
  otherDesc?: string;
  hourlyVehicles?: number[];
  needsReview?: boolean;
  enforcementRecommendedPatrolWindow?: string;
  enforcement?: {
    recommendedPatrolWindow?: string;
    [key: string]: any;
  };
};

type ChartDatum = {
  hour: number;
  vehicles: number;
  actual?: number;
};

const formatDisplayDate = (targetDate: string) => {
  const [year, month, day] = targetDate.split("-").map(Number);
  const target = new Date(year, month - 1, day);

  const today = new Date();
  const localToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const diffDays = Math.round(
    (target.getTime() - localToday.getTime()) / (1000 * 60 * 60 * 24)
  );

  const baseWithYear = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(target);

  const baseWithWeekday = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: target.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
  }).format(target);

  if (diffDays === 0) {
    return `Today (${baseWithYear})`;
  }
  if (diffDays === 1) {
    return `Tomorrow (${baseWithYear})`;
  }
  if (diffDays === -1) {
    return `Yesterday (${baseWithYear})`;
  }
  return baseWithWeekday;
};

const formatHourTick = (hour: number) => {
  const date = new Date();
  date.setHours(hour, 0, 0, 0);
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
  }).format(date);
};

const parseTimeToHour = (value: string): number | null => {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  const match = trimmed.match(
    /(\d{1,2})(?::(\d{2}))?\s*(am|pm|AM|PM)?/i
  );
  if (!match) return null;

  let hour = parseInt(match[1], 10);
  const minutes = match[2] ? parseInt(match[2], 10) : 0;
  const period = match[3]?.toLowerCase();

  if (period === "pm" && hour < 12) {
    hour += 12;
  }
  if (period === "am" && hour === 12) {
    hour = 0;
  }

  if (hour < 0 || hour > 24) return null;
  return hour + minutes / 60;
};

const parsePatrolWindow = (
  rawWindow?: string
): { startHour: number; endHour: number } | null => {
  if (!rawWindow) return null;
  if (rawWindow.toLowerCase() === "none") return null;
  const [startRaw, endRaw] = rawWindow.split("-");
  if (!startRaw || !endRaw) return null;

  const startHour = parseTimeToHour(startRaw);
  const endHour = parseTimeToHour(endRaw);

  if (startHour == null || endHour == null) return null;

  if (endHour <= startHour) {
    return {
      startHour,
      endHour: Math.min(startHour + 2, 24),
    };
  }

  return { startHour, endHour };
};

type DeltaPolarity = "positive" | "negative" | "neutral";

const getDeltaInfo = (value?: number): { numeric: number; polarity: DeltaPolarity } => {
  const numeric = Math.round((value ?? 0) * 100);
  if (Math.abs(numeric) <= 1) {
    return { numeric, polarity: "neutral" };
  }
  if (numeric > 0) return { numeric, polarity: "positive" };
  return { numeric, polarity: "negative" };
};

const getWeatherIcon = (description?: string): LucideIcon => {
  const lower = description?.toLowerCase() || "";
  if (lower.includes("thunder") || lower.includes("lightning") || lower.includes("storm")) {
    return CloudLightning;
  }
  if (lower.includes("rain")) {
    return CloudRain;
  }
  if (lower.includes("snow")) {
    return CloudSnow;
  }
  if (lower.includes("heat") || lower.includes("sunny") || lower.includes("sun")) {
    return Sun;
  }
  if (lower.includes("wind")) {
    return Wind;
  }
  if (lower.includes("fog")) {
    return CloudFog;
  }
  return CloudSun;
};

const normalizeFactorDescription = (description?: string, delta?: number) => {
  const trimmed = (description || "").trim();
  const startsWithNo = trimmed.toLowerCase().startsWith("no ");
  const deltaPct = Math.round((delta ?? 0) * 100);
  if (!trimmed) return "None";
  if (startsWithNo) return "None";
  if (Math.abs(deltaPct) <= 1) return "None";
  return trimmed;
};

const AnimatedText: React.FC<{ value: MotionValue<string> }> = ({ value }) => {
  const [display, setDisplay] = useState(value.get());
  useEffect(() => {
    const unsubscribe = value.on("change", setDisplay);
    return () => {
      unsubscribe();
    };
  }, [value]);
  return <>{display}</>;
};

const AnimatedDelta: React.FC<{ value: number; polarity: DeltaPolarity }> = ({ value, polarity }) => {
  const spring = useSpring(value, { stiffness: 500, damping: 60, mass: 0.6 });
  useEffect(() => {
    spring.set(value);
  }, [spring, value]);

  const formatted = useTransform(spring, (val) => {
    const rounded = Math.round(val);
    if (rounded > 0) return `+${rounded}%`;
    if (rounded < 0) return `${rounded}%`;
    return "0%";
  });

  return (
    <span className={`parallel-ai-factor-delta parallel-ai-delta-${polarity}`}>
      <AnimatedText value={formatted} />
    </span>
  );
};

const ParallelAI: React.FC = () => {
  const { lotId } = useParams<{ lotId: string }>();
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false);
  const [isDateMenuOpen, setIsDateMenuOpen] = useState(false);

  const {
    data: predictions,
    isLoading: isListLoading,
    error: listError,
  } = useQuery<PredictionSummary[], Error>({
    queryKey: ["aiPredictions", lotId],
    queryFn: () => aiPredictionService.getAllPredictions(lotId || ""),
    enabled: !!lotId,
    staleTime: 5 * 60 * 1000,
  });

  const sortedPredictions = useMemo(() => {
    if (!predictions) return [];
    return [...predictions].sort((a, b) =>
      b.targetDate.localeCompare(a.targetDate)
    );
  }, [predictions]);

  useEffect(() => {
    if (!sortedPredictions.length) {
      setSelectedDate("");
      setHasInitializedSelection(false);
      setIsDateMenuOpen(false);
      return;
    }

    const firstAvailable = sortedPredictions[0].targetDate;

    if (!hasInitializedSelection) {
      const todayISO = new Date().toISOString().split("T")[0];
      const todayEntry = sortedPredictions.find(
        (entry) => entry.targetDate === todayISO
      );
      const initialDate = todayEntry ? todayEntry.targetDate : firstAvailable;
      setSelectedDate(initialDate);
      setHasInitializedSelection(true);
      setIsDateMenuOpen(false);
      return;
    }

    if (!selectedDate || !sortedPredictions.some((p) => p.targetDate === selectedDate)) {
      setSelectedDate(firstAvailable);
    }
  }, [sortedPredictions, selectedDate, hasInitializedSelection]);

  const {
    data: predictionDetail,
    isLoading: isDetailLoading,
    error: detailError,
  } = useQuery<PredictionDetail | null, Error>({
    queryKey: ["aiPredictionByDate", lotId, selectedDate],
    queryFn: () =>
      aiPredictionService.getPredictionByDate(lotId || "", selectedDate),
    enabled: !!lotId && !!selectedDate,
    staleTime: 60 * 1000,
  });

  const chartData: ChartDatum[] = useMemo(() => {
    if (!predictionDetail?.hourlyVehicles) return [];
    return predictionDetail.hourlyVehicles.slice(0, 24).map((vehicles, hour) => ({
      hour,
      vehicles,
    }));
  }, [predictionDetail?.hourlyVehicles]);

  const rawPatrolWindow = useMemo(() => {
    if (!predictionDetail) return null;
    return (
      predictionDetail.recommendedPatrolWindow ??
      predictionDetail.enforcement?.recommendedPatrolWindow ??
      predictionDetail.enforcementRecommendedPatrolWindow ??
      null
    );
  }, [
    predictionDetail,
    predictionDetail?.recommendedPatrolWindow,
    predictionDetail?.enforcement?.recommendedPatrolWindow,
    predictionDetail?.enforcementRecommendedPatrolWindow,
  ]);

  const patrolWindow = useMemo(() => {
    if (!rawPatrolWindow) return null;
    const parsed = parsePatrolWindow(rawPatrolWindow);
    if (!parsed) return null;
    const { startHour, endHour } = parsed;
    const effectiveEnd = Math.min(endHour, 23.99);
    if (startHour >= effectiveEnd) return null;
    return { startHour, endHour: effectiveEnd };
  }, [rawPatrolWindow]);

  useEffect(() => {
    if (rawPatrolWindow) {
      console.debug("ParallelAI: raw patrol window", rawPatrolWindow, "parsed", patrolWindow);
    } else {
      console.debug("ParallelAI: no patrol window available");
    }
  }, [rawPatrolWindow, patrolWindow]);

  const overlayConfig = useMemo(() => {
    if (!patrolWindow) return null;
    const totalHours = 24;
    const left = Math.max(0, Math.min(patrolWindow.startHour, totalHours));
    const right = Math.max(left, Math.min(patrolWindow.endHour, totalHours));
    const width = right - left;
    const MIN_WIDTH_PERCENT = (0.5 / totalHours) * 100; // ensure visible when window is very small
    const leftPercent = (left / totalHours) * 100;
    const widthPercent = Math.max((width / totalHours) * 100, MIN_WIDTH_PERCENT);
    const clampedWidth = Math.min(widthPercent, 100 - leftPercent);
    let label: string | null = "Suggested Patrol Window";
    if (clampedWidth < 12) {
      label = null;
    } else if (clampedWidth < 20) {
      label = "Patrol window";
    } else if (clampedWidth < 28) {
      label = "Patrol";
    }
    return {
      style: {
        left: `${leftPercent}%`,
        width: `${clampedWidth}%`,
      } as React.CSSProperties,
      label,
    };
  }, [patrolWindow]);

  const isLoading = isListLoading || isDetailLoading;
  const hasError = Boolean(listError || detailError);
  const errorMessage =
    listError?.message ??
    detailError?.message ??
    "Unable to load Parallel AI predictions right now. Please try again shortly.";

  const { data: actualHourlyData } = useLotHourlyData(
    lotId || "",
    selectedDate,
    "day"
  );

  const actualHourlyMap = useMemo(() => {
    const mapped = new Map<number, number>();
    const rows: any[] = Array.isArray(actualHourlyData?.data) ? actualHourlyData!.data : [];

    rows.forEach((entry, index) => {
      let hour: number | null = null;

      if (typeof entry.hour === "number" && Number.isFinite(entry.hour)) {
        hour = entry.hour;
      } else if (typeof entry.hour === "string" && entry.hour.trim() !== "") {
        const parsedHour = parseInt(entry.hour, 10);
        if (!Number.isNaN(parsedHour)) {
          hour = parsedHour;
        } else {
          const parsedDate = new Date(entry.hour);
          if (!Number.isNaN(parsedDate.getTime())) {
            hour = parsedDate.getHours();
          }
        }
      }

      if (hour === null) {
        const timestamp = entry.timestamp ?? entry.time ?? entry.hourTimestamp ?? entry.recordedAt;
        if (timestamp) {
          const parsedTimestamp = typeof timestamp === "number" ? new Date(timestamp) : new Date(timestamp);
          if (!Number.isNaN(parsedTimestamp.getTime())) {
            hour = parsedTimestamp.getHours();
          }
        }
      }

      if (hour === null) {
        hour = Number.isFinite(index) ? index : null;
      }

      if (hour === null || Number.isNaN(hour)) return;
      hour = Math.max(0, Math.min(23, Math.floor(hour)));

      const valueCandidates = [
        entry.totalOccupancy,
        entry.occupancy,
        entry.endingOccupancy,
        entry.totalVehiclesInLot,
        entry.vehiclesInLot,
        entry.totalVehiclesAtEndOfHour,
        entry.totalVehiclesThisHour,
        entry.totalVehicles,
        entry.actualVehicles,
        entry.value,
      ];

      let value: number | undefined;
      for (const candidate of valueCandidates) {
        if (candidate === null || candidate === undefined) continue;
        const parsed = typeof candidate === "number" ? candidate : parseFloat(candidate);
        if (Number.isFinite(parsed)) {
          value = parsed;
          break;
        }
      }

      if (value === undefined) {
        value = 0;
      }

      mapped.set(hour, value);
    });

    return mapped;
  }, [actualHourlyData?.data]);

  const chartDataWithActuals: ChartDatum[] = useMemo(() => {
    return chartData.map((datum) => ({
      ...datum,
      actual: actualHourlyMap.get(datum.hour),
    }));
  }, [chartData, actualHourlyMap]);

  const barCategoryGapValue = useMemo(() => {
    const count = chartDataWithActuals.length;
    if (count >= 24) return "6%";
    if (count >= 18) return "10%";
    if (count >= 12) return "14%";
    return "20%";
  }, [chartDataWithActuals.length]);

  const overlayBarGapValue = useMemo(() => {
    const count = chartDataWithActuals.length;
    if (count >= 24) return "-84%";
    if (count >= 18) return "-74%";
    if (count >= 12) return "-64%";
    return "-50%";
  }, [chartDataWithActuals.length]);

  const renderActualBar = useCallback((props: BarProps) => {
    const xPos = typeof props.x === "string" ? parseFloat(props.x) : props.x ?? 0;
    const yPos = typeof props.y === "string" ? parseFloat(props.y) : props.y ?? 0;
    const barWidth = typeof props.width === "string" ? parseFloat(props.width) : props.width ?? 0;
    const barHeight = typeof props.height === "string" ? parseFloat(props.height) : props.height ?? 0;
    if (!Number.isFinite(barHeight) || !Number.isFinite(barWidth) || barHeight <= 0 || barWidth <= 0) {
      return <rect x={xPos} y={yPos} width={0} height={0} />;
    }
    const overlayWidth = barWidth * 1;
    const centerX = xPos + barWidth / 2;
    const adjustedX = centerX - overlayWidth / 2;
    return (
      <rect
        x={adjustedX}
        y={yPos}
        width={overlayWidth}
        height={barHeight}
        rx={4}
        ry={4}
        fill="rgba(23, 130, 255, 1)" 
        stroke="rgba(23, 130, 255, 0)"
        strokeWidth={1.2}
      />
    );
  }, []);

  const handleSelectDate = useCallback((date: string) => {
    setSelectedDate(date);
    setIsDateMenuOpen(false);
  }, []);

  const dateMenuItems: MenuItem[] = useMemo(() => {
    return sortedPredictions.map((prediction) => ({
      key: prediction.predictionId,
      label: formatDisplayDate(prediction.targetDate),
      selected: prediction.targetDate === selectedDate,
      onSelect: () => handleSelectDate(prediction.targetDate),
    }));
  }, [sortedPredictions, selectedDate, handleSelectDate]);

  const factors: Array<{
    key: string;
    label: string;
    delta?: number;
    description: string;
    icon: LucideIcon;
  }> = [
    {
      key: "weather",
      label: "Weather",
      delta: predictionDetail?.factorsWeatherDeltaPct,
      description: predictionDetail?.weatherDesc || "No weather factors identified.",
      icon: getWeatherIcon(predictionDetail?.weatherDesc),
    },
    {
      key: "alerts",
      label: "Alerts",
      delta: predictionDetail?.factorsAlertsDeltaPct,
      description:
        predictionDetail?.alertsDesc || "No alerts are impacting demand.",
      icon: TriangleAlert,
    },
    {
      key: "events",
      label: "Events",
      delta: predictionDetail?.factorsEventsDeltaPct,
      description:
        predictionDetail?.eventsDesc || "No nearby events are affecting demand.",
      icon: CalendarDays,
    },
    {
      key: "other",
      label: "Other",
      delta: predictionDetail?.factorsOtherDeltaPct,
      description:
        predictionDetail?.otherDesc || "No additional factors were detected.",
      icon: Sparkles,
    },
  ];

  const renderTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;
    const predictedEntry = payload.find((item: any) => item.dataKey === "vehicles");
    const actualEntry = payload.find((item: any) => item.dataKey === "actual");
    const data: ChartDatum = (predictedEntry || payload[0]).payload;
    return (
      <div className="parallel-ai-tooltip">
        <span className="parallel-ai-tooltip-hour">{formatHourTick(data.hour)}</span>
        {predictedEntry && (
          <span className="parallel-ai-tooltip-value">
            Predicted: {predictedEntry.value.toLocaleString()} vehicles
          </span>
        )}
        {actualEntry && typeof actualEntry.value === "number" && actualEntry.value > 0 && (
          <span className="parallel-ai-tooltip-value">
            Actual: {actualEntry.value.toLocaleString()} vehicles
          </span>
        )}
      </div>
    );
  };

  const mainContent = hasError ? (
    <div className="parallel-ai-page">
      <Error message={errorMessage} />
    </div>
  ) : (
    <div className="parallel-ai-page">
      <div className="sticky-header">
        <div className="parallel-ai-header">
          <div className="parallel-ai-header-bar">
            <div className="parallel-ai-title">
              <h1>Parallel AI</h1>
              <span className="parallel-ai-beta-chip">Beta</span>
            </div>
            <div className="parallel-ai-controls">
              <div className={`parallel-ai-date-select${isDateMenuOpen ? " open" : ""}`}>
                <div className="menu-wrapper">
                  <Menu
                    trigger={
                      <button
                        type="button"
                        className="button-secondary"
                        disabled={!sortedPredictions.length}
                      >
                        <History className="parallel-ai-date-icon" aria-hidden />
                        <span>{selectedDate ? formatDisplayDate(selectedDate) : "Select date"}</span>
                      </button>
                    }
                    items={dateMenuItems}
                    placement="bottom-start"
                    onOpenChange={setIsDateMenuOpen}
                    allowNoSelection={false}
                    maxHeight="60vh"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="parallel-ai-loading">
          <LoadingLogo text="Loading AI prediction..." />
        </div>
      )}

      {!isLoading && predictionDetail && (
        <>
          <div className="parallel-ai-forecast-card">
            <div className="parallel-ai-card-header">
              <div>
                <h2 className="parallel-ai-card-title">
                  {formatDisplayDate(predictionDetail.targetDate)}
                </h2>
                {predictionDetail.title && (
                  <p className="parallel-ai-card-subtitle">{predictionDetail.title}</p>
                )}
              </div>
              {predictionDetail.needsReview && (
                <span className="parallel-ai-review-badge">Needs review</span>
              )}
            </div>
            <div className="parallel-ai-chart-wrapper">
              {overlayConfig && (
                <div
                  className="parallel-ai-patrol-overlay"
                  style={overlayConfig.style}
                  aria-hidden="true"
                >
                  {overlayConfig.label && (
                    <span className="parallel-ai-patrol-label">{overlayConfig.label}</span>
                  )}
                </div>
              )}
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartDataWithActuals}
                  margin={{ top: 20, right: 24, bottom: 0, left: 24 }}
                  barCategoryGap={barCategoryGapValue}
                  barGap={overlayBarGapValue}
                >
                  <defs>
                    <linearGradient id="parallelAiBarFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgba(23,130,255,0.24)" />
                      <stop offset="100%" stopColor="rgba(23,130,255,0.03)" />
                    </linearGradient>
                    <linearGradient id="parallelAiBarFillWhite" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="10%" stopColor="rgba(255,255,255,0.3)" />
                      <stop offset="100%" stopColor="rgba(255,255,255,0.1)" />
                    </linearGradient>
                    <linearGradient id="parallelAiPatrolFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgba(255,82,82,0.16)" />
                      <stop offset="100%" stopColor="rgba(255,82,82,0.03)" />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="hour"
                    type="number"
                    domain={[0, 23]}
                    tickFormatter={formatHourTick}
                    ticks={[0, 3, 6, 9, 12, 15, 18, 21]}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis hide />
                  <Tooltip cursor={{ fill: "rgba(23,130,255,0.08)" }} content={renderTooltip} />
                  <Bar
                    dataKey="actual"
                    maxBarSize={40}
                    shape={renderActualBar}
                    isAnimationActive={false}
                  />
                  <Bar
                    dataKey="vehicles"
                    radius={[4, 4, 4, 4]}
                    shape={(props: { x?: number | string; y?: number | string; width?: number | string; height?: number | string; payload?: ChartDatum }) => {
                      const { x, y, width, height, payload } = props;
                      const xPos = typeof x === "string" ? parseFloat(x) : x ?? 0;
                      const yPos = typeof y === "string" ? parseFloat(y) : y ?? 0;
                      const barWidth = typeof width === "string" ? parseFloat(width) : width ?? 0;
                      const barHeight = typeof height === "string" ? parseFloat(height) : height ?? 0;

                      if (!Number.isFinite(barHeight) || !Number.isFinite(barWidth) || barHeight <= 0 || barWidth <= 0) {
                        return <rect x={xPos} y={yPos} width={0} height={0} />;
                      }

                      const data = payload as ChartDatum | undefined;
                      const actualValue = data?.actual;
                      const predictedValue = data?.vehicles;
                      const hasActual = typeof actualValue === "number" && typeof predictedValue === "number";

                      let fill = "url(#parallelAiBarFill)";
                      let stroke = "var(--parallel-blue)";
                      let opacity = 1;
                      let strokeDasharray = "6 6";

                      if (hasActual && actualValue > predictedValue) {
                        fill = "url(#parallelAiBarFillWhite)";
                        stroke = "rgba(255, 255, 255, 0.5)";
                        strokeDasharray = "6 6";
                      } else if (hasActual && actualValue <= predictedValue) {
                        opacity = 0.5;
                      }

                      const overlayWidth = barWidth * 1;
                      const centerX = xPos + barWidth / 2;
                      const adjustedX = centerX - overlayWidth / 2;

                      return (
                        <rect
                          x={adjustedX}
                          y={yPos}
                          width={overlayWidth}
                          height={barHeight}
                          rx={4}
                          ry={4}
                          fill={fill}
                          stroke={stroke}
                          strokeWidth={1.5}
                          strokeDasharray={strokeDasharray}
                          opacity={opacity}
                        />
                      );
                    }}
                    maxBarSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="parallel-ai-factors-grid">
            {factors.map((factor) => {
              const { numeric, polarity } = getDeltaInfo(factor.delta);
              const normalizedDescription = normalizeFactorDescription(factor.description, factor.delta);
              const IconComponent = factor.icon;
              return (
                <div className="parallel-ai-factor-card" key={factor.key}>
                  <div className="parallel-ai-factor-header">
                    <IconComponent className="parallel-ai-factor-icon" aria-hidden />
                    <span className="parallel-ai-factor-label">{factor.label}</span>
                  </div>
                  <AnimatedDelta value={numeric} polarity={polarity} />
                  <p className="parallel-ai-factor-description">{normalizedDescription}</p>
                </div>
              );
            })}
          </div>
        </>
      )}

      {!isLoading && !predictionDetail && !hasError && (
        <div className="parallel-ai-empty">
          <p>No AI predictions are available for this lot yet.</p>
        </div>
      )}
    </div>
  );

  return mainContent;
};

export default ParallelAI;
